import { useState, useMemo } from 'react';
import { useSystemQueries } from '../../hooks/unified/useSystemQueries';
import { useViewport } from '../../hooks/useViewport';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import type { FileItem } from '../../services/unified/systemModule';

export function FileManager() {
  useViewport(); // For responsive behavior
  const { listFiles, getStorageStats, deleteFile, deleteFiles } = useSystemQueries();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [filesToDelete, setFilesToDelete] = useState<string[]>([]);

  // Fetch files
  const { 
    data: filesData, 
    isLoading: filesLoading, 
    isError: filesError,
    refetch: refetchFiles 
  } = listFiles.useQuery({
    search: searchQuery || undefined,
  });

  // Ensure files is always an array
  const files = Array.isArray(filesData) ? filesData : [];

  // Fetch storage stats
  const { 
    data: stats, 
    isLoading: statsLoading,
    isError: statsError 
  } = getStorageStats.useQuery();

  // Filter files based on search
  const filteredFiles = useMemo(() => {
    if (!searchQuery) return files;
    
    const query = searchQuery.toLowerCase();
    return files.filter(
      (file) =>
        file.name.toLowerCase().includes(query) ||
        file.type.toLowerCase().includes(query)
    );
  }, [files, searchQuery]);

  // Handle file selection
  const toggleFileSelection = (path: string) => {
    const newSelection = new Set(selectedFiles);
    if (newSelection.has(path)) {
      newSelection.delete(path);
    } else {
      newSelection.add(path);
    }
    setSelectedFiles(newSelection);
  };

  const selectAll = () => {
    setSelectedFiles(new Set(filteredFiles.map((f) => f.path)));
  };

  const deselectAll = () => {
    setSelectedFiles(new Set());
  };

  // Handle delete
  const handleDeleteClick = (paths: string[]) => {
    setFilesToDelete(paths);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      if (filesToDelete.length === 1) {
        await deleteFile.mutateAsync(filesToDelete[0]);
      } else {
        await deleteFiles.mutateAsync({ paths: filesToDelete });
      }
      
      setShowDeleteConfirm(false);
      setFilesToDelete([]);
      setSelectedFiles(new Set());
      refetchFiles();
    } catch (error) {
      console.error('Failed to delete files:', error);
    }
  };

  // Format file size
  const formatSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get file icon
  const getFileIcon = (type: string) => {
    if (type.startsWith('image/')) {
      return (
        <svg className="h-5 w-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      );
    }
    if (type === 'application/pdf') {
      return (
        <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    }
    return (
      <svg className="h-5 w-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  if (filesLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show error message if there's an issue loading files
  if (filesError || statsError) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">File Manager</h2>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Unable to Load Files</h3>
                <p className="mt-2 text-sm text-yellow-700">
                  There was an issue loading files. This could be due to:
                </p>
                <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                  <li>AWS S3 credentials are invalid or expired</li>
                  <li>S3 bucket doesn't exist or is inaccessible</li>
                  <li>IAM permissions are insufficient</li>
                  <li>Network connectivity issues</li>
                </ul>
                <p className="mt-3 text-sm text-yellow-700">
                  <strong>To fix:</strong> Go to{' '}
                  <a href="/admin/settings" className="text-yellow-800 underline hover:text-yellow-900">
                    System Settings → Integrations
                  </a>{' '}
                  and test your AWS S3 connection, or switch storage type to "Local" for development.
                </p>
                <button
                  onClick={() => refetchFiles()}
                  className="mt-3 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Stats */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">File Manager</h2>
        
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-blue-600 font-medium">Total Files</div>
              <div className="text-lg sm:text-2xl font-bold text-blue-900">{stats.file_count}</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3 sm:p-4">
              <div className="text-xs sm:text-sm text-green-600 font-medium">Total Size</div>
              <div className="text-lg sm:text-2xl font-bold text-green-900">{formatSize(stats.total_size)}</div>
            </div>
            {stats.quota && (
              <div className="bg-purple-50 rounded-lg p-3 sm:p-4 col-span-2 sm:col-span-1">
                <div className="text-xs sm:text-sm text-purple-600 font-medium">Storage Used</div>
                <div className="text-lg sm:text-2xl font-bold text-purple-900">
                  {((stats.total_size / stats.quota) * 100).toFixed(1)}%
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Search and Actions */}
      <div className="bg-white rounded-lg shadow p-3 sm:p-4">
        <div className="flex flex-col gap-3">
          <input
            type="text"
            placeholder="Search files..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[44px]"
          />
          
          {selectedFiles.size > 0 && (
            <div className="flex gap-2">
              <button
                onClick={() => handleDeleteClick(Array.from(selectedFiles))}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm"
              >
                Delete ({selectedFiles.size})
              </button>
              <button
                onClick={deselectAll}
                className="flex-1 sm:flex-none px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px] text-sm"
              >
                Deselect
              </button>
            </div>
          )}
        </div>
      </div>

      {/* File List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Mobile: Card layout */}
        <div className="sm:hidden">
          {/* Select all header */}
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <label className="flex items-center gap-2 min-h-[44px]">
              <input
                type="checkbox"
                checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
              />
              <span className="text-sm text-gray-600">Select all</span>
            </label>
            <span className="text-xs text-gray-500">{filteredFiles.length} files</span>
          </div>
          
          {filteredFiles.length === 0 ? (
            <div className="px-4 py-12 text-center text-gray-500">
              {searchQuery ? 'No files found.' : 'No files found.'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredFiles.map((file) => (
                <div key={file.path} className="p-4">
                  <div className="flex gap-3">
                    {/* Checkbox */}
                    <div className="flex-shrink-0 pt-1">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.path)}
                        onChange={() => toggleFileSelection(file.path)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 w-5 h-5"
                      />
                    </div>
                    
                    {/* Thumbnail for images */}
                    {file.type.startsWith('image/') ? (
                      <button
                        onClick={() => setPreviewFile(file)}
                        className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-gray-100 border border-gray-200"
                      >
                        <img
                          src={file.url}
                          alt={file.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </button>
                    ) : (
                      <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center">
                        {getFileIcon(file.type)}
                      </div>
                    )}
                    
                    {/* File info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5 truncate">{file.type}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <span className="font-medium">{formatSize(file.size)}</span>
                        <span>•</span>
                        <span>{new Date(file.uploaded_at).toLocaleDateString()}</span>
                      </div>
                      {file.uploaded_by && (
                        <p className="text-xs text-gray-400 mt-0.5">by {file.uploaded_by}</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Action buttons - full width row */}
                  <div className="flex gap-2 mt-3 ml-8">
                    {file.type.startsWith('image/') && (
                      <button
                        onClick={() => setPreviewFile(file)}
                        className="flex-1 px-3 py-2.5 bg-blue-50 text-blue-600 text-sm font-medium rounded-lg min-h-[44px] flex items-center justify-center gap-1.5"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                        View
                      </button>
                    )}
                    <a
                      href={file.url}
                      download
                      className="flex-1 px-3 py-2.5 bg-primary-50 text-primary-600 text-sm font-medium rounded-lg min-h-[44px] flex items-center justify-center gap-1.5"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download
                    </a>
                    <button
                      onClick={() => handleDeleteClick([file.path])}
                      className="px-3 py-2.5 bg-red-50 text-red-600 text-sm font-medium rounded-lg min-h-[44px] flex items-center justify-center"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Desktop: Table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedFiles.size === filteredFiles.length && filteredFiles.length > 0}
                    onChange={(e) => e.target.checked ? selectAll() : deselectAll()}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Uploaded By
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFiles.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    {searchQuery ? 'No files found matching your search.' : 'No files found.'}
                  </td>
                </tr>
              ) : (
                filteredFiles.map((file) => (
                  <tr key={file.path} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedFiles.has(file.path)}
                        onChange={() => toggleFileSelection(file.path)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        {getFileIcon(file.type)}
                        <span className="ml-2 text-sm font-medium text-gray-900">{file.name}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{file.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatSize(file.size)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(file.uploaded_at)}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{file.uploaded_by || 'Unknown'}</td>
                    <td className="px-6 py-4 text-right text-sm font-medium space-x-2">
                      {file.type.startsWith('image/') && (
                        <button
                          onClick={() => setPreviewFile(file)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Preview
                        </button>
                      )}
                      <a
                        href={file.url}
                        download
                        className="text-primary-600 hover:text-primary-900"
                      >
                        Download
                      </a>
                      <button
                        onClick={() => handleDeleteClick([file.path])}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 safe-area-inset">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
            <div className="sticky top-0 p-4 border-b border-gray-200 flex justify-between items-center bg-white">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-4">{previewFile.name}</h3>
              <button
                onClick={() => setPreviewFile(null)}
                className="text-gray-400 hover:text-gray-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4">
              {previewFile.type.startsWith('image/') && (
                <img
                  src={previewFile.url}
                  alt={previewFile.name}
                  className="max-w-full h-auto mx-auto"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 safe-area-inset">
          <div className="bg-white rounded-t-2xl sm:rounded-lg w-full sm:max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Deletion</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete {filesToDelete.length} file(s)? This action cannot be undone.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setFilesToDelete([]);
                }}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors min-h-[44px]"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteFile.isPending || deleteFiles.isPending}
                className="w-full sm:w-auto px-4 py-3 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 min-h-[44px]"
              >
                {deleteFile.isPending || deleteFiles.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
